import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Upload, Loader2, Send, Copy, ListChecks, X, Heart, CheckCircle2, MessageSquare,
  Image as ImageIcon, Paintbrush, Settings as SettingsIcon, Activity,
  Lock, Download, Star, ExternalLink, Eye, EyeOff, Trash2, FolderPlus, Folder, Plus, Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "./Dashboard";
import { copyToClipboardWithToast } from "@/lib/clipboard";

const FILE_BASE = process.env.REACT_APP_BACKEND_URL;
const COVER_LAYOUTS = ["center", "left", "novel", "vintage", "frame", "stripe", "divider", "journal"];

export default function GalleryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("photos"); // photos | design | settings | activities

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/galleries/${id}`);
      setG(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-scroll to the SelectionsBlock when the URL hash is #selections.
  // Triggered by the "View selected photos" button in the notification email so the
  // studio owner lands directly on the client's favorites instead of having to scroll.
  useEffect(() => {
    if (!g || g.selections?.length === 0) return;
    if (window.location.hash !== "#selections") return;
    setTab("photos");
    const t = setTimeout(() => {
      document.getElementById("selections")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [g]);

  async function saveSettings(patch) {
    try {
      const { data } = await api.patch(`/admin/galleries/${id}`, patch);
      setG((prev) => ({ ...prev, ...data }));
      toast.success("Saved");
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendShare() {
    if (!g.customer_email) return toast.error("Add a client email in Settings first");
    if (!window.confirm(`Email gallery link to ${g.customer_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/galleries/${id}/send`);
      toast.success("Share link emailed");
      try { await copyToClipboardWithToast(data.share_url, "Share URL copied"); } catch (err) { console.warn("clipboard copy failed", err); }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function markDelivered() {
    if (!g.download_password) {
      return toast.error("Set a download password in Settings first, then mark as delivered.");
    }
    if (!window.confirm("Mark this collection as DELIVERED? Clients will be able to download high-res files using the download password.")) return;
    await saveSettings({ status: "delivered", mode: "delivery" });
  }

  if (loading || !g) {
    return <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  const isFolder = !!g.parent_gallery_id;
  const backTo = isFolder
    ? `/admin/galleries/${g.parent_gallery_id}`
    : g.client_id
      ? `/admin/galleries/clients/${g.client_id}`
      : "/admin/galleries";
  const backLabel = isFolder
    ? `Back to ${g.parent?.name || "collection"}`
    : g.client_id ? "Back to client" : "All collections";

  return (
    <div className="space-y-6" data-testid="gallery-detail-page">
      {/* Header */}
      <div>
        <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-galleries">
          <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
        </button>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2">
              {isFolder && <Folder className="w-3 h-3" />}
              {isFolder ? "Folder" : (g.customer_name || "Collection")}
            </div>
            <h1 className="font-serif text-4xl font-light" data-testid="gallery-title">{g.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge value={g.status} />
              {(g.included_edits || 0) > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-border text-muted-foreground">
                  {g.included_edits} included · ${Number(g.extra_edit_price || 0).toFixed(0)}/extra
                </span>
              )}
              {(g.category_tags || []).map((t) => (
                <span key={t} className="px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] bg-muted">{t}</span>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={g.share_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em]" data-testid="preview-public-btn">
              <Eye className="w-4 h-4" /> Preview
            </a>
            <button onClick={sendShare} className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90" data-testid="send-gallery-btn">
              <Send className="w-4 h-4" /> Share
            </button>
            {g.status !== "delivered" && (
              <button onClick={markDelivered} className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-700 hover:bg-emerald-800 text-white text-xs uppercase tracking-[0.18em]" data-testid="mark-delivered-btn">
                <CheckCircle2 className="w-4 h-4" /> Mark delivered
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Share kit — link + auto-generated passwords for folders.
          Top-level collections keep the existing minimal link bar. */}
      {isFolder ? (
        <ShareKitPanel g={g} />
      ) : (
        <div className="bg-card border border-border px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <span className="eyebrow whitespace-nowrap">Public link</span>
          <code className="flex-1 text-xs truncate w-full sm:w-auto" data-testid="gallery-share-url">{g.share_url}</code>
          <div className="flex gap-2">
            <button onClick={() => copyToClipboardWithToast(g.share_url, "Copied")} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60" data-testid="copy-share-url">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <a href={g.share_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* Folders section — only on top-level collections. Each folder is a self-contained
          sub-gallery with its own slug, password, downloads etc. so the photographer can
          share "Edited" separately from "Proofs". */}
      {!isFolder && <FoldersSection g={g} reload={load} />}

      {/* Selections-received banner — surfaces submissions that would otherwise be
          hidden below the photo grid. Tapping "View" switches to the Photos tab and
          smoothly scrolls down to the SelectionsBlock anchor. */}
      {(g.selections?.length || 0) > 0 && (
        <button
          onClick={() => {
            setTab("photos");
            // Wait a tick for the tab content to mount before scrolling
            setTimeout(() => {
              document.getElementById("selections")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
          }}
          className="w-full text-left bg-rose-50 border border-rose-200 hover:border-rose-300 px-5 py-4 flex items-center gap-3"
          data-testid="selections-banner"
        >
          <Heart className="w-5 h-5 text-rose-500 fill-rose-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-rose-900">
              {g.selections.length} client selection{g.selections.length > 1 ? "s" : ""} received
            </div>
            <div className="text-xs text-rose-700/80 truncate">
              {g.selections.map((s) => `${s.client_name} (${s.photo_ids.length})`).join(" · ")}
            </div>
          </div>
          <span className="text-xs uppercase tracking-[0.18em] text-rose-700 hover:text-rose-900 whitespace-nowrap">View →</span>
        </button>
      )}

      {/* Tabbed layout */}
      <div className="grid lg:grid-cols-[200px_1fr] gap-6">
        {/* Left rail */}
        <aside className="bg-card border border-border h-fit">
          <TabBtn icon={ImageIcon} label="Photos" active={tab === "photos"} onClick={() => setTab("photos")} testid="tab-photos" />
          <TabBtn icon={Paintbrush} label="Design" active={tab === "design"} onClick={() => setTab("design")} testid="tab-design" />
          <TabBtn icon={SettingsIcon} label="Settings" active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings" />
          <TabBtn icon={Activity} label="Activities" active={tab === "activities"} onClick={() => setTab("activities")} testid="tab-activities" />
        </aside>

        <div className="min-w-0">
          {tab === "photos" && <PhotosTab g={g} reload={load} />}
          {tab === "design" && <DesignTab g={g} save={saveSettings} />}
          {tab === "settings" && <SettingsTab g={g} save={saveSettings} reload={load} onDeleted={() => navigate(backTo)} />}
          {tab === "activities" && <ActivitiesTab g={g} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 ${
        active ? "border-foreground bg-muted text-foreground" : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ----------------- PHOTOS TAB ----------------- */
function PhotosTab({ g, reload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [count, setCount] = useState({ done: 0, total: 0 });

  async function handleFiles(files) {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    setCount({ done: 0, total: list.length });
    let done = 0, failed = 0;

    const CONCURRENCY = 6;

    const uploadOne = async (file) => {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/galleries/${g.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        failed++;
        toast.error(`${file.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"}`);
      } finally {
        done++;
        setCount({ done, total: list.length });
      }
    };

    // Concurrency-limited parallel uploads (originals preserved, no compression)
    const queue = [...list];
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const next = queue.shift();
        if (next) await uploadOne(next);
      }
    });
    await Promise.all(workers);

    setUploading(false);
    if (done - failed > 0) toast.success(`Uploaded ${done - failed} photo${done - failed > 1 ? "s" : ""}`);
    reload();
    if (fileRef.current) fileRef.current.value = "";
  }

  async function deletePhoto(photoId) {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/galleries/${g.id}/photos/${photoId}`);
      toast.success("Photo removed");
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function setCover(fileId) {
    try {
      await api.patch(`/admin/galleries/${g.id}`, { cover_image: `/api/files/${fileId}` });
      toast.success("Cover updated");
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  return (
    <section className="bg-card border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl">Photos</h2>
          <div className="text-xs text-muted-foreground mt-0.5">{g.photos?.length || 0} uploaded</div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="upload-photos-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-60" data-testid="upload-photos-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? `Uploading ${count.done}/${count.total}…` : "Upload photos"}
        </button>
      </div>

      {g.photos.length === 0 ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className="border border-dashed border-border p-16 text-center text-muted-foreground cursor-pointer hover:border-foreground/40"
          data-testid="photo-drop-zone"
        >
          <Upload className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-serif text-2xl text-foreground/60">Add the first photos</div>
          <div className="text-sm mt-1">Drag & drop or click — JPG, PNG, WebP, HEIC up to 25 MB each</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="photo-grid">
          {g.photos.map((p) => {
            const url = `${FILE_BASE}/api/files/${p.file_id}?w=600&q=78`;
            const isCover = g.cover_image && g.cover_image.endsWith(p.file_id);
            return (
              <div key={p.id} className="relative group aspect-square bg-muted overflow-hidden">
                <img src={url} alt={p.original_filename} className="w-full h-full object-cover" loading="lazy" />
                {isCover && <div className="absolute top-1.5 left-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] bg-white/95 text-foreground inline-flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-foreground" /> cover</div>}
                <div className="absolute inset-0 bg-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-1.5 gap-1">
                  <button onClick={() => setCover(p.file_id)} className="px-2 h-7 text-[10px] uppercase tracking-[0.15em] bg-white/95 hover:bg-white text-foreground" data-testid={`set-cover-${p.id}`}>
                    Set cover
                  </button>
                  <button onClick={() => deletePhoto(p.id)} className="w-7 h-7 bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center" data-testid={`delete-photo-${p.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selections inline preview */}
      {g.selections.length > 0 && <SelectionsBlock g={g} />}
    </section>
  );
}

/* ----------------- DESIGN TAB ----------------- */
function DesignTab({ g, save }) {
  const [layout, setLayout] = useState(g.cover_layout || "center");
  useEffect(() => setLayout(g.cover_layout || "center"), [g.cover_layout]);

  async function apply(l) {
    setLayout(l);
    await save({ cover_layout: l });
  }

  return (
    <section className="bg-card border border-border p-6 space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Cover design</h2>
        <p className="text-xs text-muted-foreground mt-1">Pick the layout your client sees when they open the public link.</p>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="cover-layout-picker">
        {COVER_LAYOUTS.map((l) => (
          <button
            key={l}
            onClick={() => apply(l)}
            className={`group bg-stone-50 border ${layout === l ? "border-foreground ring-1 ring-foreground" : "border-border hover:border-foreground/60"}`}
            data-testid={`cover-layout-${l}`}
          >
            <div className="aspect-[3/4] p-3 bg-stone-100 relative overflow-hidden">
              <CoverPreviewMini layout={l} coverImage={g.cover_image} />
            </div>
            <div className="p-2 text-xs capitalize text-center border-t border-border">{l}</div>
          </button>
        ))}
      </div>

      <div>
        <div className="eyebrow mb-3">Live preview</div>
        <div className="bg-stone-100 border border-border" data-testid="cover-live-preview">
          <CoverPreview layout={layout} coverImage={g.cover_image} name={g.name} studioName="Allure Image" customerName={g.customer_name} />
        </div>
      </div>
    </section>
  );
}

function CoverPreviewMini({ layout, coverImage }) {
  const bg = coverImage ? `url(${FILE_BASE}${coverImage})` : "linear-gradient(135deg,#d6d3d1,#a8a29e)";
  switch (layout) {
    case "center":
      return <div className="w-full h-full bg-cover bg-center flex items-center justify-center" style={{ backgroundImage: bg }}>
        <div className="text-[10px] tracking-widest bg-white/80 px-2 py-1">TITLE</div>
      </div>;
    case "left":
      return <div className="w-full h-full bg-cover bg-center flex items-end" style={{ backgroundImage: bg }}>
        <div className="text-[10px] tracking-widest bg-white/80 px-2 py-1 m-1">TITLE</div>
      </div>;
    case "novel":
      return <div className="w-full h-full flex items-end">
        <div className="w-2/3 h-2/3 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="flex-1 text-[8px] p-1">TITLE</div>
      </div>;
    case "vintage":
      return <div className="w-full h-full bg-cover bg-center flex items-end relative" style={{ backgroundImage: bg }}>
        <div className="absolute inset-x-2 bottom-1 text-[10px] text-white text-center border-t border-white pt-1">TITLE</div>
      </div>;
    case "frame":
      return <div className="w-full h-full bg-white p-2 flex items-center justify-center">
        <div className="w-full h-full bg-cover bg-center relative border border-stone-700" style={{ backgroundImage: bg }}>
          <div className="absolute inset-x-0 bottom-1 text-[10px] tracking-widest text-white text-center">TITLE</div>
        </div>
      </div>;
    case "stripe":
      return <div className="w-full h-full bg-cover bg-center relative" style={{ backgroundImage: bg }}>
        <div className="absolute inset-x-0 top-2 h-px bg-white/80" />
        <div className="absolute inset-x-0 bottom-2 h-px bg-white/80" />
        <div className="absolute inset-0 flex items-center justify-center text-[10px] tracking-widest text-white">TITLE</div>
      </div>;
    case "divider":
      return <div className="w-full h-full flex flex-col">
        <div className="flex-1 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="h-1/4 bg-white text-[10px] flex items-center justify-center tracking-widest">TITLE</div>
      </div>;
    case "journal":
      return <div className="w-full h-full flex">
        <div className="w-1/2 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="w-1/2 bg-white text-[10px] flex items-center justify-center tracking-widest">TITLE</div>
      </div>;
    default:
      return null;
  }
}

function CoverPreview({ layout, coverImage, name, studioName, customerName }) {
  return (
    <div className="aspect-[16/9]">
      <CoverHero layout={layout} coverImage={coverImage} name={name} studioName={studioName} customerName={customerName} preview />
    </div>
  );
}

// Reusable cover renderer (also used by public Gallery)
export function CoverHero({ layout, coverImage, name, studioName, customerName, preview = false }) {
  const bg = coverImage ? `${FILE_BASE}${coverImage}` : null;
  const bgStyle = bg ? { backgroundImage: `url(${bg})` } : { background: "linear-gradient(135deg,#d6d3d1,#a8a29e)" };
  const text = (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{studioName}</div>
      <div className={`font-serif font-light ${preview ? "text-3xl" : "text-5xl md:text-6xl"} mt-2`}>{name}</div>
      {customerName && <div className="text-xs mt-2 opacity-80">For {customerName}</div>}
    </>
  );

  if (layout === "center") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative h-full flex items-center justify-center text-center text-white px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "left") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        <div className="relative h-full flex items-end justify-start text-left text-white px-8 pb-8">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "novel") {
    return (
      <div className="w-full h-full flex bg-stone-50">
        <div className="w-2/3 bg-cover bg-center" style={bgStyle} />
        <div className="w-1/3 flex items-end p-6 text-stone-900">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "vintage") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative h-full flex flex-col items-center justify-end text-center text-white px-6 pb-10">
          <div className="border-t border-b border-white/80 py-4 px-8 max-w-3xl w-full">{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "frame") {
    return (
      <div className="w-full h-full bg-stone-50 p-4 sm:p-6">
        <div className="relative w-full h-full bg-cover bg-center border-2 border-stone-900/10" style={bgStyle}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative h-full flex items-end justify-center text-center text-white px-6 pb-6">
            <div>{text}</div>
          </div>
        </div>
      </div>
    );
  }
  if (layout === "stripe") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-x-8 top-6 h-px bg-white/70" />
        <div className="absolute inset-x-8 bottom-6 h-px bg-white/70" />
        <div className="relative h-full flex items-center justify-center text-center text-white px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "divider") {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 bg-cover bg-center" style={bgStyle} />
        <div className="h-1/4 bg-stone-50 flex items-center justify-center text-center text-stone-900 px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "journal") {
    return (
      <div className="w-full h-full flex">
        <div className="w-1/2 bg-cover bg-center" style={bgStyle} />
        <div className="w-1/2 bg-stone-50 flex items-center justify-center text-center px-6 text-stone-900">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  return null;
}

/* ----------------- SETTINGS TAB ----------------- */
function SettingsTab({ g, save, reload, onDeleted }) {
  const [section, setSection] = useState("general"); // general | privacy | download | favorite

  const sections = [
    { key: "general", label: "General", icon: SettingsIcon },
    { key: "privacy", label: "Privacy", icon: Lock },
    { key: "download", label: "Download", icon: Download },
    { key: "favorite", label: "Favorite", icon: Heart },
  ];

  async function deleteGallery() {
    if (!window.confirm(`Permanently delete "${g.name}" and all its photos? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/galleries/${g.id}`);
      toast.success("Collection deleted");
      onDeleted();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  return (
    <section className="bg-card border border-border">
      <div className="grid sm:grid-cols-[180px_1fr]">
        <nav className="border-b sm:border-b-0 sm:border-r border-border">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`settings-sub-${s.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 ${
                  section === s.key ? "border-foreground bg-muted" : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            );
          })}
        </nav>

        <div className="p-6">
          {section === "general" && <GeneralSettings g={g} save={save} reload={reload} onDelete={deleteGallery} />}
          {section === "privacy" && <PrivacySettings g={g} save={save} />}
          {section === "download" && <DownloadSettings g={g} save={save} />}
          {section === "favorite" && <FavoriteSettings g={g} save={save} />}
        </div>
      </div>
    </section>
  );
}

function GeneralSettings({ g, save, onDelete }) {
  const [form, setForm] = useState({
    name: g.name, customer_name: g.customer_name || "", customer_email: g.customer_email || "",
    welcome_message: g.welcome_message || "",
    category_tags: (g.category_tags || []).join(", "),
    expires_at: g.expires_at ? new Date(g.expires_at).toISOString().slice(0, 10) : "",
  });
  useEffect(() => {
    setForm({
      name: g.name, customer_name: g.customer_name || "", customer_email: g.customer_email || "",
      welcome_message: g.welcome_message || "",
      category_tags: (g.category_tags || []).join(", "),
      expires_at: g.expires_at ? new Date(g.expires_at).toISOString().slice(0, 10) : "",
    });
  }, [g]);

  async function submit() {
    const tags = form.category_tags.split(",").map((s) => s.trim()).filter(Boolean);
    const patch = {
      name: form.name, customer_name: form.customer_name, customer_email: form.customer_email,
      welcome_message: form.welcome_message,
      category_tags: tags,
    };
    // expires_at: convert YYYY-MM-DD to end-of-day ISO; empty string clears it
    if (form.expires_at) {
      patch.expires_at = new Date(`${form.expires_at}T23:59:59`).toISOString();
    } else {
      patch.expires_at = "";
    }
    await save(patch);
  }

  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl">General settings</h3>
      <In label="Collection name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="set-general-name" />
      <div className="grid sm:grid-cols-2 gap-3">
        <In label="Client name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="set-general-client-name" />
        <In label="Client email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="set-general-client-email" />
      </div>
      <In label="Category tags (comma-separated)" value={form.category_tags} onChange={(v) => setForm({ ...form, category_tags: v })} testid="set-general-tags" placeholder="maternity, family, brand" />
      <In label="Auto-expire date" type="date" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} testid="set-general-expires" />
      <label className="block">
        <span className="eyebrow">Welcome message</span>
        <textarea rows={3} value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm" data-testid="set-general-welcome" />
      </label>
      <div className="flex justify-between pt-3 border-t border-border">
        <button onClick={onDelete} className="inline-flex items-center gap-1.5 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-destructive/40 text-destructive hover:bg-destructive/5" data-testid="delete-gallery-btn">
          <Trash2 className="w-3.5 h-3.5" /> Delete collection
        </button>
        <button onClick={submit} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-general-btn">Save</button>
      </div>
    </div>
  );
}

function PrivacySettings({ g, save }) {
  const [form, setForm] = useState({ password: g.password || "", max_selections: g.max_selections || 0 });
  useEffect(() => setForm({ password: g.password || "", max_selections: g.max_selections || 0 }), [g]);
  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl">Privacy</h3>
      <p className="text-xs text-muted-foreground">Galleries always require a view password. Share it with your client by email.</p>
      <In label="View password *" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="set-privacy-password" />
      <In label="Max selections (0 = unlimited)" type="number" value={form.max_selections} onChange={(v) => setForm({ ...form, max_selections: v })} testid="set-privacy-max-sel" />
      <div className="flex justify-end pt-3 border-t border-border">
        <button onClick={() => save({ password: form.password, max_selections: Number(form.max_selections) || 0 })} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-privacy-btn">Save</button>
      </div>
    </div>
  );
}

function DownloadSettings({ g, save }) {
  const [form, setForm] = useState({
    downloads_enabled: g.downloads_enabled !== false,
    download_password: g.download_password || "",
  });
  useEffect(() => setForm({ downloads_enabled: g.downloads_enabled !== false, download_password: g.download_password || "" }), [g]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">Download</h3>
        <Toggle on={form.downloads_enabled} onChange={(v) => setForm({ ...form, downloads_enabled: v })} testid="set-download-toggle" />
      </div>
      <p className="text-xs text-muted-foreground">Clients can only download once the collection is marked as <strong>delivered</strong> and they enter the download password below.</p>
      <In label="Download password" value={form.download_password} onChange={(v) => setForm({ ...form, download_password: v })} testid="set-download-password" />
      <div className="flex justify-end pt-3 border-t border-border">
        <button onClick={() => save({ downloads_enabled: form.downloads_enabled, download_password: form.download_password })} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-download-btn">Save</button>
      </div>
    </div>
  );
}

function FavoriteSettings({ g, save }) {
  const [form, setForm] = useState({
    favorites_enabled: g.favorites_enabled !== false,
    included_edits: g.included_edits || 0,
    extra_edit_price: g.extra_edit_price ?? 35,
  });
  useEffect(() => setForm({
    favorites_enabled: g.favorites_enabled !== false,
    included_edits: g.included_edits || 0,
    extra_edit_price: g.extra_edit_price ?? 35,
  }), [g]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">Favorites &amp; edits</h3>
        <Toggle on={form.favorites_enabled} onChange={(v) => setForm({ ...form, favorites_enabled: v })} testid="set-fav-toggle" />
      </div>
      <p className="text-xs text-muted-foreground">When enabled, clients can favorite photos for editing and leave per-photo notes.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <In label="Included edits (from package)" type="number" value={form.included_edits} onChange={(v) => setForm({ ...form, included_edits: v })} testid="set-fav-included" />
        <In label="Extra edit price ($)" type="number" value={form.extra_edit_price} onChange={(v) => setForm({ ...form, extra_edit_price: v })} testid="set-fav-extra-price" />
      </div>
      <div className="flex justify-end pt-3 border-t border-border">
        <button
          onClick={() => save({ favorites_enabled: form.favorites_enabled, included_edits: Number(form.included_edits) || 0, extra_edit_price: Number(form.extra_edit_price) || 0 })}
          className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]"
          data-testid="save-fav-btn"
        >Save</button>
      </div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`relative w-12 h-6 rounded-full transition-colors ${on ? "bg-emerald-600" : "bg-stone-300"}`}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? "translate-x-6" : ""}`} />
    </button>
  );
}

/* ----------------- ACTIVITIES TAB ----------------- */
function ActivitiesTab({ g }) {
  return (
    <section className="bg-card border border-border p-6" data-testid="activities-tab">
      <h2 className="font-serif text-2xl mb-1">Activities</h2>
      <p className="text-xs text-muted-foreground mb-6">A summary of what&apos;s happened on this collection.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total photos" value={g.photos?.length || 0} icon={ImageIcon} />
        <Stat label="Selections received" value={g.selections?.length || 0} icon={ListChecks} />
        <Stat label="Status" value={g.status} icon={CheckCircle2} />
      </div>
      <div className="mt-6">
        <h3 className="font-serif text-lg mb-3">Timeline</h3>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> Created {new Date(g.created_at).toLocaleString()}</li>
          {g.sent_at && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> Shared with client {new Date(g.sent_at).toLocaleString()}</li>}
          {g.selections?.length > 0 && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> {g.selections.length} client submission{g.selections.length > 1 ? "s" : ""}</li>}
          {g.delivered_at && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Delivered {new Date(g.delivered_at).toLocaleString()}</li>}
        </ul>
      </div>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-border p-4">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="font-serif text-3xl mt-2 capitalize">{value}</div>
    </div>
  );
}

/* ----------------- Selections inline ----------------- */
function SelectionsBlock({ g }) {
  const photoMap = Object.fromEntries(g.photos.map((p) => [p.id, p]));
  return (
    <div id="selections" className="mt-8 pt-6 border-t border-border scroll-mt-24" data-testid="selections-block">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks className="w-4 h-4" />
        <h3 className="font-serif text-xl">Client selections</h3>
      </div>
      <div className="space-y-6">
        {g.selections.map((s) => {
          const commentsByPhoto = Object.fromEntries((s.items || []).map((it) => [it.photo_id, it.comment]));
          const totalsLine = (s.included_edits || 0) > 0
            ? <>{s.included_edits} included · {s.extra_edit_count || 0} extra × ${Number(s.extra_edit_price || 0).toFixed(2)} = <strong className="text-foreground">${Number(s.extra_edit_total || 0).toFixed(2)}</strong></>
            : null;
          return (
            <div key={s.id} className="border-t border-border pt-4 first:border-t-0 first:pt-0" data-testid={`selection-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  <div className="font-medium text-sm">{s.client_name} {s.client_email && <span className="text-muted-foreground">· {s.client_email}</span>}</div>
                  <div className="text-xs text-muted-foreground">{new Date(s.submitted_at).toLocaleString()} · {s.photo_ids.length} favorited</div>
                  {totalsLine && <div className="text-xs text-muted-foreground mt-0.5">{totalsLine}</div>}
                </div>
                <button
                  onClick={() => {
                    const names = s.photo_ids
                      .map((pid) => photoMap[pid]?.original_filename)
                      .filter(Boolean)
                      .join("\n");
                    const n = names.split("\n").length;
                    copyToClipboardWithToast(names, `Copied ${n} filename${n > 1 ? "s" : ""} to clipboard`);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 hover:bg-muted shrink-0"
                  data-testid={`copy-filenames-${s.id}`}
                  title="Copy every favorited filename — paste into Finder/Explorer search to locate your raw files"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy filenames
                </button>
              </div>
              {s.message && <div className="text-sm italic text-muted-foreground mb-3">&ldquo;{s.message}&rdquo;</div>}
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {s.photo_ids.map((pid) => {
                  const photo = photoMap[pid];
                  if (!photo) return null;
                  const comment = commentsByPhoto[pid];
                  const fname = photo.original_filename || "";
                  return (
                    <div key={pid} className="bg-card border border-border overflow-hidden" data-testid={`selection-photo-${pid}`}>
                      <div className="aspect-square bg-muted relative">
                        <img src={`${FILE_BASE}/api/files/${photo.file_id}?w=400&q=78`} alt={fname} className="w-full h-full object-cover" loading="lazy" />
                        <Heart className="w-4 h-4 absolute bottom-1.5 right-1.5 text-rose-500 fill-rose-500" />
                      </div>
                      {fname && (
                        <button
                          onClick={() => copyToClipboardWithToast(fname, "Filename copied")}
                          className="w-full text-left px-2 py-1.5 text-[11px] font-mono truncate hover:bg-muted border-t border-border flex items-center gap-1"
                          data-testid={`copy-filename-${pid}`}
                          title={`Click to copy "${fname}"`}
                        >
                          <Copy className="w-3 h-3 text-muted-foreground shrink-0" />
                          <span className="truncate">{fname}</span>
                        </button>
                      )}
                      {comment && (
                        <div className="p-2 text-[11px] italic border-t border-border">
                          <MessageSquare className="w-3 h-3 inline mr-1 text-muted-foreground" /> &ldquo;{comment}&rdquo;
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function In({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={testid}
      />
    </label>
  );
}


/* ----------------- FOLDERS SECTION ----------------- */
function FoldersSection({ g, reload }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const folders = g.folders || [];

  if (folders.length === 0 && !creating) {
    return (
      <div className="bg-card border border-border px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="folders-empty">
        <div className="flex items-center gap-3">
          <FolderPlus className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Organize with folders</div>
            <div className="text-xs text-muted-foreground">
              Create folders inside this collection to share edited photos separately, or split by event day.
            </div>
          </div>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
          data-testid="add-folder-btn-empty"
        >
          <Plus className="w-3.5 h-3.5" /> New folder
        </button>
        <NewFolderDialog open={creating} onClose={() => setCreating(false)} parentId={g.id} existingCount={folders.length} onCreated={() => { setCreating(false); reload(); }} />
      </div>
    );
  }

  return (
    <section className="bg-card border border-border p-5" data-testid="folders-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Folder className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-serif text-xl">Folders</h2>
          <span className="text-xs text-muted-foreground">({folders.length})</span>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
          data-testid="add-folder-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New folder
        </button>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {folders.map((f) => (
          <button
            key={f.id}
            onClick={() => navigate(`/admin/galleries/${f.id}`)}
            className="text-left bg-stone-50 border border-border hover:border-foreground/60 transition-colors group relative"
            data-testid={`folder-card-${f.id}`}
          >
            {(f.selection_count || 0) > 0 && (
              <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.15em]">
                <Heart className="w-3 h-3 fill-white" /> {f.selection_count}
              </span>
            )}
            <div className="aspect-[4/3] bg-muted overflow-hidden">
              {f.cover_image ? (
                <img src={`${FILE_BASE}${f.cover_image}`} alt={f.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                  <Folder className="w-8 h-8" />
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="font-serif text-sm truncate">{f.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {f.photo_count || 0} photo{f.photo_count === 1 ? "" : "s"}
                {f.password ? " · 🔒" : ""}
                {f.downloads_enabled === false ? " · view only" : ""}
              </div>
            </div>
          </button>
        ))}
      </div>

      <NewFolderDialog open={creating} onClose={() => setCreating(false)} parentId={g.id} existingCount={folders.length} onCreated={() => { setCreating(false); reload(); }} />
    </section>
  );
}

function NewFolderDialog({ open, onClose, parentId, onCreated, existingCount = 0 }) {
  const navigate = useNavigate();
  // Two presets that match the photographer's actual workflow.
  // The default selection auto-flips based on whether this is the 1st or 2nd folder.
  const PRESETS = {
    selections: {
      id: "selections",
      icon: Heart,
      name: "Client Selections",
      tagline: "Client picks the photos they want edited",
      body: "Best for the first folder. Clients see the proofs, tap hearts on the ones they want edited, and submit. Downloads on with auto-password so they can save high-res copies too.",
      defaults: { name: "Client Selections", favorites_enabled: true, downloads_enabled: true, mode: "proofing" },
    },
    edited: {
      id: "edited",
      icon: Sparkles,
      name: "Edited Photos",
      tagline: "Share the finished, edited gallery",
      body: "Best for the second folder. Final retouched photos clients can browse and download with the password you'll get after creating.",
      defaults: { name: "Edited Photos", favorites_enabled: false, downloads_enabled: true, mode: "delivery" },
    },
  };

  // Auto-pick the right preset for this slot
  const defaultPreset = existingCount === 0 ? "selections" : "edited";
  const [presetId, setPresetId] = useState(defaultPreset);
  const [name, setName] = useState(PRESETS[defaultPreset].defaults.name);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const p = existingCount === 0 ? "selections" : "edited";
      setPresetId(p);
      setName(PRESETS[p].defaults.name);
      setSubmitting(false);
    }
  }, [open, existingCount]);

  function pickPreset(id) {
    setPresetId(id);
    setName(PRESETS[id].defaults.name);
  }

  async function submit() {
    if (submitting) return;
    if (!name.trim()) return toast.error("Folder name is required");
    setSubmitting(true);
    try {
      const preset = PRESETS[presetId];
      // Step 1: create the folder. Backend auto-generates both passwords because
      // we omit them in the payload. Folder is created with default flags;
      // step 2 below applies the preset's flag overrides.
      const { data } = await api.post("/admin/galleries", {
        name: name.trim(),
        parent_gallery_id: parentId,
      });
      // Step 2: patch the preset's flag overrides (favorites / downloads / mode).
      const patches = {};
      if (preset.defaults.favorites_enabled !== undefined && preset.defaults.favorites_enabled !== data.favorites_enabled) {
        patches.favorites_enabled = preset.defaults.favorites_enabled;
      }
      if (preset.defaults.downloads_enabled !== undefined && preset.defaults.downloads_enabled !== data.downloads_enabled) {
        patches.downloads_enabled = preset.defaults.downloads_enabled;
      }
      if (preset.defaults.mode && preset.defaults.mode !== data.mode) {
        patches.mode = preset.defaults.mode;
      }
      if (Object.keys(patches).length > 0) {
        await api.patch(`/admin/galleries/${data.id}`, patches);
      }
      toast.success(`Folder "${data.name}" created — password ready`);
      onCreated();
      navigate(`/admin/galleries/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light flex items-center gap-2">
            <FolderPlus className="w-5 h-5" /> New folder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5" data-testid="new-folder-form">
          {/* Preset picker */}
          <div className="space-y-2">
            <div className="eyebrow">What&apos;s this folder for?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.values(PRESETS).map((p) => {
                const Icon = p.icon;
                const active = p.id === presetId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPreset(p.id)}
                    className={`text-left p-4 border-2 transition-all ${active ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"}`}
                    data-testid={`folder-preset-${p.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className={`w-4 h-4 ${active ? "text-foreground" : "text-muted-foreground"}`} />
                      <div className="font-medium text-sm">{p.name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">{p.tagline}</div>
                    <div className="text-[11px] text-muted-foreground/80 leading-relaxed">{p.body}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <In label="Folder name (edit if you like)" value={name} onChange={setName} testid="folder-name" />

          <div className="bg-muted/30 border border-border p-3 text-xs text-muted-foreground flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              A unique <strong>view password</strong> and <strong>download password</strong> will be generated automatically.
              You&apos;ll see both at the top of the folder page — ready to copy & send to your client.
            </span>
          </div>

          <DialogFooter className="gap-2">
            <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
            <button
              onClick={submit}
              disabled={submitting}
              className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground disabled:opacity-60"
              data-testid="create-folder-btn"
            >
              {submitting ? "Creating…" : "Create folder"}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- SHARE KIT PANEL (folder pages) -----------------
   Shows the public link + auto-generated view & download passwords, with
   one-click copy buttons + a "Copy share kit" master button that puts a
   nicely-formatted text block on the clipboard ready to paste into an
   email/SMS for the client. */
function ShareKitPanel({ g }) {
  const [showView, setShowView] = useState(false);
  const [showDl, setShowDl] = useState(false);

  const folderLabel = g.name || "Gallery";
  const lines = [
    `${folderLabel}`,
    `Link: ${g.share_url}`,
  ];
  if (g.password) lines.push(`Password: ${g.password}`);
  if (g.download_password && g.downloads_enabled !== false) lines.push(`Download password: ${g.download_password}`);
  const blockText = lines.join("\n");

  return (
    <section
      className="bg-gradient-to-br from-card to-muted/30 border border-border p-5 space-y-4"
      data-testid="share-kit-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Share kit
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Send your client this link and the password{g.download_password && g.downloads_enabled !== false ? "s" : ""} so they can {g.favorites_enabled ? "pick their favorites" : "view & download"}.
          </div>
        </div>
        <button
          onClick={() => copyToClipboardWithToast(blockText, "Share kit copied — paste it into your message")}
          className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90"
          data-testid="copy-share-kit-btn"
        >
          <Copy className="w-4 h-4" /> Copy share kit
        </button>
      </div>

      {/* Link row */}
      <ShareKitRow
        label="Link"
        icon={ExternalLink}
        value={g.share_url}
        masked={false}
        testid="sk-link"
        trailing={
          <a
            href={g.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
            title="Open in new tab"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        }
      />

      {/* View password row — only if present (folders always have one; top-level may not) */}
      {g.password && (
        <ShareKitRow
          label="View password"
          icon={Lock}
          value={g.password}
          masked={!showView}
          onToggleMask={() => setShowView((v) => !v)}
          testid="sk-view-pw"
        />
      )}

      {/* Download password row — only if downloads enabled */}
      {g.download_password && g.downloads_enabled !== false && (
        <ShareKitRow
          label="Download password"
          icon={Download}
          value={g.download_password}
          masked={!showDl}
          onToggleMask={() => setShowDl((v) => !v)}
          testid="sk-dl-pw"
        />
      )}

      {!g.password && !g.download_password && (
        <div className="text-xs text-muted-foreground italic">
          No passwords set — this gallery is publicly accessible via the link.
        </div>
      )}
    </section>
  );
}

function ShareKitRow({ label, icon: Icon, value, masked, onToggleMask, testid, trailing }) {
  const display = masked ? "•".repeat(Math.max(6, value.length)) : value;
  return (
    <div className="flex items-center gap-3 bg-background border border-border px-3 py-2.5">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        <code className="font-mono text-sm text-foreground truncate block" data-testid={`${testid}-value`}>
          {display}
        </code>
      </div>
      {onToggleMask && (
        <button
          onClick={onToggleMask}
          className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
          title={masked ? "Show" : "Hide"}
          data-testid={`${testid}-toggle`}
        >
          {masked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      )}
      <button
        onClick={() => copyToClipboardWithToast(value, `${label} copied`)}
        className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
        title={`Copy ${label.toLowerCase()}`}
        data-testid={`${testid}-copy`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      {trailing}
    </div>
  );
}

